import { ref } from 'vue'

/**
 * Composable for managing UI expand/collapse state in Sourcing view
 */
export function useSourcingUI() {
  // Week expansion state: { weekKey: boolean }
  // Default is expanded (true), so undefined means expanded
  const expandedWeeks = ref({})

  // Prospect accordion state: { prospectId: boolean }
  const expandedProspects = ref({})

  // Availability section expansion per preference: { 'prospectId-prefId': boolean }
  const availabilityExpanded = ref({})

  // Per-employee graph expansion: { 'prospectId-prefId': { employeeId: boolean } }
  const employeeGraphExpanded = ref({})

  // ==================== Week Functions ====================

  /**
   * Toggle week expand/collapse
   * @param {string} weekKey
   */
  const toggleWeekExpand = weekKey => {
    const isCurrentlyExpanded = expandedWeeks.value[weekKey] !== false
    expandedWeeks.value[weekKey] = !isCurrentlyExpanded
  }

  /**
   * Check if week is expanded (default: true)
   * @param {string} weekKey
   * @returns {boolean}
   */
  const isWeekExpanded = weekKey => {
    return expandedWeeks.value[weekKey] !== false
  }

  // ==================== Prospect Functions ====================

  /**
   * Toggle prospect accordion expand
   * @param {number} prospectId
   * @returns {boolean} - New expanded state
   */
  const toggleExpand = prospectId => {
    expandedProspects.value[prospectId] = !expandedProspects.value[prospectId]
    return expandedProspects.value[prospectId]
  }

  /**
   * Check if prospect is expanded (default: false)
   * @param {number} prospectId
   * @returns {boolean}
   */
  const isProspectExpanded = prospectId => {
    return !!expandedProspects.value[prospectId]
  }

  /**
   * Expand a prospect
   * @param {number} prospectId
   */
  const expandProspect = prospectId => {
    expandedProspects.value[prospectId] = true
  }

  /**
   * Collapse a prospect
   * @param {number} prospectId
   */
  const collapseProspect = prospectId => {
    expandedProspects.value[prospectId] = false
  }

  // ==================== Availability Section Functions ====================

  /**
   * Toggle availability section expansion for a preference
   * @param {number} prospectId
   * @param {string|number} prefId
   */
  const toggleAvailabilityExpanded = (prospectId, prefId) => {
    const key = `${prospectId}-${prefId}`
    const isCurrentlyExpanded = availabilityExpanded.value[key] === true
    availabilityExpanded.value[key] = !isCurrentlyExpanded
  }

  /**
   * Check if availability section is expanded (default: true)
   * @param {number} prospectId
   * @param {string|number} prefId
   * @returns {boolean}
   */
  const isAvailabilityExpanded = (prospectId, prefId) => {
    const key = `${prospectId}-${prefId}`
    return availabilityExpanded.value[key] === true
  }

  /**
   * Collapse availability section for a preference
   * @param {number} prospectId
   * @param {string|number} prefId
   */
  const collapseAvailabilitySection = (prospectId, prefId) => {
    const key = `${prospectId}-${prefId}`
    availabilityExpanded.value[key] = false
  }

  // ==================== Employee Graph Functions ====================

  /**
   * Toggle individual employee graph expansion
   * @param {number} prospectId
   * @param {string|number} prefId
   * @param {string} empId
   */
  const toggleEmployeeGraph = (prospectId, prefId, empId) => {
    const key = `${prospectId}-${prefId}`
    if (!employeeGraphExpanded.value[key]) {
      employeeGraphExpanded.value[key] = {}
    }
    // If undefined (default expanded), set to false to collapse
    const currentState = employeeGraphExpanded.value[key][empId] !== false
    employeeGraphExpanded.value[key][empId] = !currentState
  }

  /**
   * Check if an employee graph is expanded (default: true)
   * @param {number} prospectId
   * @param {string|number} prefId
   * @param {string} empId
   * @returns {boolean}
   */
  const isEmployeeGraphExpanded = (prospectId, prefId, empId) => {
    const key = `${prospectId}-${prefId}`
    return employeeGraphExpanded.value[key]?.[empId] !== false
  }

  // ==================== Utility Functions ====================

  /**
   * Reset all UI state
   */
  const resetAll = () => {
    expandedWeeks.value = {}
    expandedProspects.value = {}
    availabilityExpanded.value = {}
    employeeGraphExpanded.value = {}
  }

  /**
   * Collapse all weeks
   */
  const collapseAllWeeks = () => {
    for (const key of Object.keys(expandedWeeks.value)) {
      expandedWeeks.value[key] = false
    }
  }

  /**
   * Expand all weeks
   */
  const expandAllWeeks = () => {
    expandedWeeks.value = {}
  }

  return {
    // State (exposed for v-model or direct access)
    expandedWeeks,
    expandedProspects,
    availabilityExpanded,
    employeeGraphExpanded,

    // Week functions
    toggleWeekExpand,
    isWeekExpanded,
    collapseAllWeeks,
    expandAllWeeks,

    // Prospect functions
    toggleExpand,
    isProspectExpanded,
    expandProspect,
    collapseProspect,

    // Availability section functions
    toggleAvailabilityExpanded,
    isAvailabilityExpanded,
    collapseAvailabilitySection,

    // Employee graph functions
    toggleEmployeeGraph,
    isEmployeeGraphExpanded,

    // Utility
    resetAll
  }
}
